import { useState, forwardRef, useImperativeHandle, useRef, KeyboardEvent, useEffect } from 'react';
import { Send, Loader2, Square } from 'lucide-react';
import './MessageInput.css';

interface MessageInputProps {
  onSendMessage: (message: string) => void;
  onCancel?: () => void;
  disabled: boolean;
  isStreaming?: boolean;
  placeholder?: string;
}

export interface MessageInputRef {
  focusInput: () => void;
}

const MessageInput = forwardRef<MessageInputRef, MessageInputProps>(
  ({ onSendMessage, onCancel, disabled, isStreaming = false, placeholder = "Type your message..." }, ref) => {
    const [message, setMessage] = useState('');
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    useImperativeHandle(ref, () => ({
      focusInput: () => {
        textareaRef.current?.focus();
      },
    }));

    // Automatically restore focus when input becomes enabled
    useEffect(() => {
      if (!disabled && textareaRef.current) {
        const timer = setTimeout(() => {
          textareaRef.current?.focus();
        }, 100);
        return () => clearTimeout(timer);
      }
    }, [disabled]);

    const handleSend = () => {
      if (message.trim() && !disabled) {
        onSendMessage(message.trim());
        setMessage('');
        // Reset textarea height
        if (textareaRef.current) {
          textareaRef.current.style.height = 'auto';
          // Keep focus on the input after sending
          setTimeout(() => {
            textareaRef.current?.focus();
          }, 50);
        }
      }
    };

    const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setMessage(e.target.value);

      // Auto-resize textarea
      const textarea = e.target;
      textarea.style.height = 'auto';
      textarea.style.height = Math.min(textarea.scrollHeight, 120) + 'px';
    };

    return (
      <div className="message-input-container">
        <div className="message-input-wrapper">
          <textarea
            ref={textareaRef}
            value={message}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            disabled={disabled}
            className="message-input"
            rows={1}
          />
          <button
            onClick={isStreaming ? onCancel : handleSend}
            disabled={!isStreaming && (disabled || !message.trim())}
            className={`send-button btn ${isStreaming ? 'stop-button' : ''}`}
            aria-label={isStreaming ? "Stop response" : "Send message"}
          >
            {isStreaming ? (
              <>
                <Square size={18} fill="currentColor" />
                <span className="button-label">Stop</span>
              </>
            ) : disabled ? (
              <Loader2 size={18} className="animate-spin" />
            ) : (
              <Send size={18} />
            )}
          </button>
        </div>
        <div className="input-hint">
          Press Enter to send, Shift+Enter for new line
        </div>
      </div>
    );
  }
);

MessageInput.displayName = 'MessageInput';

export default MessageInput;
